import { ThemeEvents } from '@theme/events';

/**
 * Élővé teszi a mobil menü drawer kosár-függő elemeit:
 * - a "Kosár" gombot (darabszám-jelvény + végösszeg; ha van a kosárban valami, ez a lábléc teljes
 *   szélességű elsődleges gombja, a kosár ikon pedig finoman megrázkódik, amikor a drawer megnyílik)
 * - az "Ingyenes szállítás" sávot (hátralévő összeg / "megvan az ingyenes szállítás" állapot /
 *   haladásjelző / link a kisebb ajándékokhoz)
 *
 * A cart:update esemény data.itemCount mezője az esemény forrásától függően vagy egy DELTA
 * (product-form-component esetén hozzáadandó a jelenlegi számhoz), vagy az ÚJ teljes érték
 * (minden más forrásnál) - ugyanaz a logika, mint amit a téma saját cart-icon.js-e használ
 * a fejléc kosár-buborékjához, hogy a két számláló mindig egyezzen.
 *
 * A végösszeget és a szállítás-sávot csak a megnyitott drawerben látja a vásárló, ezért zárt
 * drawernél nem kérünk le semmit a szerverről: csak megjegyezzük, hogy elavult, és a drawer
 * következő megnyitásakor frissítünk.
 */

const cartPill = document.querySelector('[data-cart-pill]');
const cartPillBadge = cartPill ? cartPill.querySelector('[data-cart-pill-badge]') : null;
const cartPillTotal = cartPill ? cartPill.querySelector('[data-cart-pill-total]') : null;
const cartPillIcon = cartPill ? cartPill.querySelector('[data-cart-pill-icon]') : null;
const footerPills = document.querySelector('[data-footer-pills]');
const shippingBanner = document.querySelector('[data-shipping-banner]');
const shippingCloser = shippingBanner ? shippingBanner.querySelector('[data-shipping-banner-closer]') : null;
const drawerDetails = document.getElementById('Details-menu-drawer-container');

let currentCartCount = cartPillBadge ? parseInt(cartPillBadge.textContent ?? '0', 10) || 0 : 0;
let cartIsStale = false;

const TRUCK_ICON_PATH =
  'M255.42,117l-14-35A15.93,15.93,0,0,0,226.58,72H192V64a8,8,0,0,0-8-8H32A16,16,0,0,0,16,72V184a16,16,0,0,0,16,16H49a32,32,0,0,0,62,0h50a32,32,0,0,0,62,0h17a16,16,0,0,0,16-16V120A7.94,7.94,0,0,0,255.42,117ZM192,88h34.58l9.6,24H192ZM32,72H176v64H32ZM80,208a16,16,0,1,1,16-16A16,16,0,0,1,80,208Zm81-24H111a32,32,0,0,0-62,0H32V152H176v12.31A32.11,32.11,0,0,0,161,184Zm31,24a16,16,0,1,1,16-16A16,16,0,0,1,192,208Zm48-24H223a32.06,32.06,0,0,0-31-24V128h48Z';
const CHECK_ICON_PATH =
  'M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z';

function setCartPillCount(count) {
  currentCartCount = count;
  if (cartPillBadge) cartPillBadge.textContent = count > 99 ? '99+' : String(count);
  // A Kosár gomb kinézetét (kis pill <-> teljes szélességű narancs gomb) ez az osztály váltja
  if (footerPills) footerPills.classList.toggle('menu-drawer__footer-pills--has-cart', count > 0);
}

function formatHuf(cents) {
  const forint = Math.round(cents / 100);
  // Saját ezres tagolás: a toLocaleString('hu-HU') a négyjegyű számokat nem tagolja ("3020"), a Liquid
  // `money` szűrő viszont igen ("3 020 Ft"), így oldalbetöltés és élő frissítés között eltért volna a szöveg.
  return String(forint).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' Ft';
}

function renderShippingBanner(cart) {
  if (!shippingBanner) return;
  const textEl = shippingBanner.querySelector('[data-shipping-banner-text]');
  const iconEl = shippingBanner.querySelector('[data-shipping-banner-icon] svg');
  const progressFillEl = shippingBanner.querySelector('[data-shipping-banner-progress-fill]');
  if (!textEl) return;
  const threshold = parseInt(shippingBanner.dataset.freeShippingThresholdCents ?? '0', 10);
  const closerMaxRemaining = parseInt(shippingBanner.dataset.shippingCloserMaxRemainingCents ?? '0', 10);
  const remaining = Math.max(threshold - cart.total_price, 0);
  const reached = cart.item_count > 0 && remaining <= 0;
  const progressPercent = threshold > 0 ? Math.min((cart.total_price / threshold) * 100, 100) : 0;

  shippingBanner.classList.toggle('menu-drawer__shipping-banner--reached', reached);
  if (iconEl) iconEl.querySelector('path').setAttribute('d', reached ? CHECK_ICON_PATH : TRUCK_ICON_PATH);
  if (progressFillEl) progressFillEl.style.width = progressPercent + '%';
  // A "kisebb ajándékok" link csak akkor kell, ha van kosár, még nincs meg a küszöb, de már közel vagyunk
  if (shippingCloser) shippingCloser.hidden = !(cart.item_count > 0 && remaining > 0 && remaining <= closerMaxRemaining);

  if (reached) {
    textEl.innerHTML =
      '<span class="shipping-banner-main">Ingyenes szállítást kaptál!</span>' +
      '<span class="shipping-banner-tagline">A kosaradra már nem számolunk szállítási díjat.</span>';
  } else if (cart.item_count > 0) {
    textEl.innerHTML =
      '<span class="shipping-banner-main">Már csak <strong>' +
      formatHuf(remaining) +
      '</strong> az ingyenes szállításig!</span>';
  } else {
    textEl.innerHTML =
      '<span class="shipping-banner-main">Ingyenes szállítás <strong>10 000 Ft</strong> felett</span>' +
      '<span class="shipping-banner-tagline">Mert az ajándékozás öröm, a szállítás pedig ajándék!</span>';
  }
}

function renderCartState(cart) {
  // A /cart.js hiteles item_count-ját is felhasználjuk, hogy a jelvény szinkronban maradjon akkor is,
  // ha az esemény data.itemCount-ja valamiért eltérne (pl. tömeges módosítás a kosár oldalon).
  if (typeof cart.item_count === 'number') {
    setCartPillCount(cart.item_count);
  }
  if (cartPillTotal) cartPillTotal.textContent = formatHuf(cart.total_price);
  renderShippingBanner(cart);
}

function fetchCartAndSync() {
  if (!cartPill && !shippingBanner) return Promise.resolve();
  return fetch('/cart.js', { headers: { Accept: 'application/json' } })
    .then((response) => response.json())
    .then(renderCartState)
    .catch(() => {
      /* csendben feladjuk - a gombnak/sávnak marad az utolsó ismert állapota */
    });
}

function playCartPillWiggle() {
  if (!cartPillIcon || currentCartCount <= 0) return;
  cartPillIcon.classList.remove('menu-drawer__footer-pill-icon-wrap--wiggle');
  // reflow, hogy az animáció újraindítható legyen akkor is, ha az előző még nem végzett
  void cartPillIcon.offsetWidth;
  cartPillIcon.classList.add('menu-drawer__footer-pill-icon-wrap--wiggle');
}

function isBackForwardNavigation() {
  const navigation = performance.getEntriesByType?.('navigation')?.[0];
  return navigation?.type === 'back_forward';
}

function onCartUpdate(event) {
  const itemCount = event.detail?.data?.itemCount;
  if (typeof itemCount === 'number') {
    const fromProductForm = event.detail?.data?.source === 'product-form-component';
    setCartPillCount(fromProductForm ? currentCartCount + itemCount : itemCount);
  }
  if (drawerDetails?.open) {
    fetchCartAndSync();
  } else {
    cartIsStale = true;
  }
}

if (cartPill || shippingBanner) {
  document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);

  // A böngésző "vissza" gombjával érkezve (bfcache-ből vagy HTTP-gyorsítótárból) az oldal a régi,
  // szerver által renderelt állapotában jöhet vissza: ha a kosár közben AJAX-szal változott, a
  // darabszám/összeg elavult (pl. 0), és a rázkódás emiatt nem indul el. Ilyenkor hitelesen újraszinkronizálunk
  // (a téma saját cart-icon.js-e is külön kezeli ugyanezt a pageshow-nál).
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted && !isBackForwardNavigation()) return;
    fetchCartAndSync().then(() => {
      // bfcache-ből a drawer nyitva is visszajöhet - ilyenkor nem indulna újra a "nyitás" esemény
      if (drawerDetails?.open) playCartPillWiggle();
    });
  });
}

// Drawer megnyitásakor: ha közben változott a kosár, frissítünk, és finoman megrázkódik a kosár ikon.
if (drawerDetails && (cartPill || shippingBanner)) {
  drawerDetails.addEventListener('toggle', () => {
    if (!drawerDetails.open) return;
    if (cartIsStale) {
      cartIsStale = false;
      fetchCartAndSync();
    }
    playCartPillWiggle();
  });
}
